import { type FC, type JSX, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { CardGridArea, Tag } from "widgets";

import { renderGeneAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

import { createSummaryCardsFromClassifications } from "../../app-utils/card.js";
import { getGeneTypeColor } from "../../app-utils/color.js";
import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { type User } from "../../graphql.js";
import { type IGeneState, shouldLoadGene } from "../../redux/modules/gene.js";
import { type IAppState } from "../../redux/reducer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { DatabaseCard } from "./cards/DatabaseCard.js";
import { GeneCard } from "./cards/GeneCard.js";
import { GenomeCard } from "./cards/GenomeCard.js";
import { ProteinListCard } from "./cards/ProteinListCard.js";
import { ReferencesCard } from "./cards/ReferencesCard.js";
import { RelatedProjectsCard } from "./cards/RelatedProjectsCard.js";
import { TranscriptListCard } from "./cards/TranscriptListCard.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";

interface IGeneSummaryViewOwnProps {
  title?: string;
  headerTags?: JSX.Element[];
}

const GeneSummaryView: FC<IGeneSummaryViewOwnProps> = (props) => {
  const { geneId } = useParams<"geneId">();
  const { gene, isLoading } = useSelector<IAppState, IGeneState>((state) => state.gene);
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadGene, geneId);

  useEffect(() => {
    void load();
  }, [load]);

  const showSpinner: boolean = isLoading || !gene;
  const tag = gene?.geneType ? <Tag label={gene.geneType} intentColor={getGeneTypeColor(gene.geneType)} /> : null;

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (gene ? gene.name : "")}
      subtitle={gene?.description || "Gene Summary"}
      entity={isLoading ? null : gene}
      headerTags={(props.headerTags || []).concat(tag)}
      headerAttributes={[
        <AttributesListWidget attributes={renderGeneAttributesWidgets(gene)} key={"gene-summary-view-widget-1"} />,
      ]}
      isLoading={showSpinner}
      getEntityName={() => gene?.name}
    >
      {gene && (
        <>
          <GeneCard gene={gene} isEntitySummary area={CardGridArea.Left} />
          <GenomeCard genome={gene.genome} area={CardGridArea.Left} />
          <DatabaseCard database={gene.database} genome={gene.genome} area={CardGridArea.Left} />
          {createSummaryCardsFromClassifications(gene.allClassifications, CardGridArea.Left)}
          <TranscriptListCard transcripts={gene.transcripts} area={CardGridArea.Right} />
          <ProteinListCard proteins={gene.proteins} area={CardGridArea.Right} />
          <RelatedProjectsCard
            relatedProjects={gene.relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
          <ReferencesCard
            title="Literature References"
            references={gene.literatureReferences}
            area={CardGridArea.Right}
          />
          <ReferencesCard references={gene.allExternalReferences} area={CardGridArea.Left} />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default GeneSummaryView;
